import client from './client';

export type NotificationType = 'proxy_expiry' | 'node_expiry' | 'domain_ip_change' | 'node_unavailable' | 'proxy_unavailable';
export type NotificationStage = 'upcoming' | 'soon' | 'urgent' | 'expired';
export type NotificationStatus = 'open' | 'resolved' | 'ignored';

export interface NotificationItem {
  id: number;
  type: NotificationType;
  proxy_id: number | null;
  proxy_user_id: number | null;
  node_id: number | null;
  stage: NotificationStage;
  title: string;
  message: string;
  proxy_note: string;
  proxy_user_nickname: string;
  wechat_id: string;
  node_name: string;
  node_ip: string;
  port: number | null;
  expire_at: string;
  days_remaining: number;
  status: NotificationStatus;
  read_at: string | null;
  resolved_at: string | null;
  resolved_reason: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationList {
  items: NotificationItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface NotificationSummary {
  unread_count: number;
  open_count: number;
  by_stage: Record<NotificationStage, number>;
}

export interface NotificationRules {
  enabled: boolean;
  node_enabled: boolean;
  upcoming_days: number;
  soon_days: number;
  urgent_days: number;
  digest_enabled: boolean;
  digest_time: string;
  timezone: string;
}

export interface SMTPSettings {
  enabled: boolean;
  host: string;
  port: number;
  security: 'ssl' | 'starttls' | 'none';
  username: string;
  from_address: string;
  from_name: string;
  recipients: string[];
  password_configured: boolean;
}

export interface NotificationSettings {
  rules: NotificationRules;
  smtp: SMTPSettings;
}

export const listNotifications = async (params: Record<string, unknown> = {}): Promise<NotificationList> =>
  (await client.get('/api/notifications', { params })).data;

export const getNotificationSummary = async (): Promise<NotificationSummary> =>
  (await client.get('/api/notifications/summary')).data;

export const readNotification = async (id: number): Promise<void> => {
  await client.post(`/api/notifications/${id}/read`);
};

export const readAllNotifications = async (): Promise<void> => {
  await client.post('/api/notifications/read-all');
};

export const ignoreNotification = async (id: number): Promise<void> => {
  await client.post(`/api/notifications/${id}/ignore`);
};

export const getNotificationSettings = async (): Promise<NotificationSettings> =>
  (await client.get('/api/settings/notifications')).data;

export const saveNotificationSettings = async (
  settings: NotificationSettings,
  smtpPassword?: string,
): Promise<{ password_configured: boolean }> => {
  const payload: Record<string, unknown> = { rules: settings.rules, smtp: settings.smtp };
  if (smtpPassword?.trim()) payload.smtp_password = smtpPassword.trim();
  return (await client.put('/api/settings/notifications', payload)).data;
};

export const testNotificationEmail = async (
  settings: NotificationSettings,
  smtpPassword?: string,
): Promise<{ sent_count: number; recipient_count: number }> => {
  const payload: Record<string, unknown> = { rules: settings.rules, smtp: settings.smtp };
  if (smtpPassword?.trim()) payload.smtp_password = smtpPassword.trim();
  return (await client.post('/api/settings/notifications/test-email', payload)).data;
};

export const clearSMTPPassword = async (): Promise<void> => {
  await client.delete('/api/settings/notifications/smtp-secret');
};
